import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit3 } from "react-icons/fi";
import {
  Currency,
  SourcingEventStatus,
  useUpdateSourcingEventValueMutation,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { formatEstimatedValue } from "../../../sourcing-events/portal/utils/displayFormatters";

const EDITABLE_STATUSES = [
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
  SourcingEventStatus.Completed,
];

export const EventValueSection = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [updateValue, { isLoading }] = useUpdateSourcingEventValueMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");

  const currency = event.eventValue?.currency ?? Currency.Nok;
  const canEdit = EDITABLE_STATUSES.includes(event.status);

  const save = async () => {
    const parsed = Number(draftValue);
    try {
      await updateValue({
        eventId: event.id,
        updateSourcingEventValueRequest: {
          eventValue:
            draftValue.trim() !== "" && !Number.isNaN(parsed) ? { value: parsed, currency } : null,
        },
      }).unwrap();
      setIsEditing(false);
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
      <Modal isOpen={isEditing} onClose={() => setIsEditing(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Edit the procurement value")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>{t("Procurement value")}</FormLabel>
              <NumberInput value={draftValue} onChange={(value) => setDraftValue(value)}>
                <NumberInputField />
              </NumberInput>
              <FormHelperText>{t("The value used in reporting, in NOK")}</FormHelperText>
            </FormControl>
          </ModalBody>
          <ModalFooter gap="3">
            <Button variant="ghost" onClick={() => setIsEditing(false)}>
              {t("Abort")}
            </Button>
            <Button colorScheme="teal" onClick={save} isLoading={isLoading}>
              {t("Save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <HStack justify="space-between" align="flex-start">
        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="1">
            {t("Procurement value")}
          </Text>
          <Text fontSize="lg" fontWeight="semibold" color="smText">
            {event.eventValue?.value != null
              ? formatEstimatedValue(event.eventValue.value, currency)
              : "–"}
          </Text>
        </Box>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FiEdit3} />}
            onClick={() => {
              setDraftValue(event.eventValue?.value != null ? String(event.eventValue.value) : "");
              setIsEditing(true);
            }}
          >
            {t("Edit")}
          </Button>
        )}
      </HStack>
    </Box>
  );
};
